/**
 * [CP-11 / PR1 identity-canonicalization-ssot] Identifier-key normalization for
 * the by-identifier CRM status path.
 *
 * People-search results and People/Contacts directory rows are NOT yet
 * Cleanlist leads (no lead_id), so their live CRM status is resolved by
 * IDENTIFIER (LinkedIn URL, optional secondary email) via
 * `POST /crm/status/by-identifier`. The BE keys its response `statuses` map
 * through the identity SSOT `canonical_linkedin_url`
 * (cleanse_api/providers/crm/crm_normalize.py — re-exported from
 * cleanse_api/utils/identity.py), so the FE MUST derive the SAME canonical key.
 *
 * `crmStatusIdentifierKey` mirrors `canonical_linkedin_url` exactly — the ONE
 * canonical person-profile form across the whole stack:
 *   - lowercase the whole URL, trim whitespace,
 *   - keep only LinkedIn person profiles (`/in/<slug>` or `/pub/<slug>`),
 *   - collapse `www.`/locale subdomains to bare `linkedin.com`,
 *   - drop query string + fragment + trailing slash,
 *   - emit `https://linkedin.com/<verb>/<slug>`.
 *
 * Returns `null` for company/school pages, non-LinkedIn hosts, or junk
 * ("n/a", a typed name) — which the caller drops and never tries to match.
 * This replaces the old byte-for-byte mirror of `normalize_linkedin_url` (the
 * www/trailing-slash/case-preserving form): there is now ONE shape.
 */
const _LINKEDIN_PERSON_PATH_RE = /^\/(in|pub)\/([^/?#]+)/i;

export function crmStatusIdentifierKey(
  url: string | null | undefined
): string | null {
  if (!url || typeof url !== 'string') return null;
  let v = url.trim().toLowerCase();
  if (!v) return null;
  if (!v.includes('://')) {
    v = `https://${v}`;
  }
  let parsed: URL;
  try {
    parsed = new URL(v);
  } catch {
    return null;
  }
  // Strip any userinfo + port to get the bare host.
  const host = parsed.host.split('@').pop()?.split(':')[0] ?? '';
  if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) {
    return null;
  }
  const match = _LINKEDIN_PERSON_PATH_RE.exec(parsed.pathname || '');
  if (!match) return null;
  const verb = match[1];
  const slug = match[2];
  return `https://linkedin.com/${verb}/${slug}`;
}

/** A single identifier the by-identifier endpoint resolves. */
export interface CrmIdentifierInput {
  linkedinUrl: string | null | undefined;
  email?: string | null;
}

/** Wire-shaped identifier matching BE `IdentifierItem`. */
export interface CrmIdentifierWire {
  linkedin_url: string;
  email?: string | null;
}

/**
 * Map a page's worth of rows to the deduped, stable-sorted batch request body
 * the by-identifier endpoint expects, plus a per-row key resolver.
 *
 * - Drops rows whose LinkedIn URL doesn't normalize to a key (the BE can only
 *   key by LinkedIn; an email-only row can't be looked up).
 * - Dedupes by normalized key (first-seen wins, so the first row's email is the
 *   secondary signal sent for that key).
 * - Sorts identifiers by key so the request args are canonical → one stable RTK
 *   cache entry per (crm, page) regardless of row order or scroll.
 */
export function buildCrmIdentifierBatch(
  rows: ReadonlyArray<CrmIdentifierInput>
): { identifiers: CrmIdentifierWire[] } {
  const byKey = new Map<string, CrmIdentifierWire>();
  for (const row of rows) {
    const key = crmStatusIdentifierKey(row.linkedinUrl);
    if (!key) continue;
    if (byKey.has(key)) continue;
    const email = row.email?.trim() ? row.email.trim().toLowerCase() : null;
    byKey.set(key, {
      // Send the RAW (untrimmed-of-scheme) LinkedIn URL the BE re-normalizes;
      // it owns the canonical key. We send the normalized key so request +
      // response keys are guaranteed identical even if the BE normalizer drifts.
      linkedin_url: key,
      ...(email ? { email } : {}),
    });
  }
  const identifiers = Array.from(byKey.values()).sort((a, b) =>
    a.linkedin_url < b.linkedin_url
      ? -1
      : a.linkedin_url > b.linkedin_url
        ? 1
        : 0
  );
  return { identifiers };
}
